import { ConsentPolicy } from '@wix/cookie-consent-policy-client';
import { any } from 'prop-types';
import { SettingsLayout } from '../../../constants/Layout';
import { MediaType } from '../../../constants/MediaType';
import { NameSpace } from '../../../constants/NameSpace';
import { Selection } from '../../../constants/Selection';
import { Sort } from '../../../constants/Sort';
import { ExternalSource, MainSource } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import {
  MediaManagerSDKFile,
  MediaManagerSDKManagerInfo,
  MediaManagerSDKTeam,
  MediaManagerSDKSitesInfo,
} from '../../media-manager-sdk';
import { Acl } from '../../../constants/Acl';

export const contextTypes = {
  mmgrSettings: any,
  latestIncomingMMGRSettings: any,
};

export interface ConsentPolicyChangedMessage {
  data: ConsentPolicy;
}

export type TMMGRDebugSettings = {
  alwaysShowAddMediaContributorsHint: boolean;
  alwaysShowUploadingContributorsHint: boolean;
};

type TMMGRCommonSettings = Partial<TMMGRDebugSettings> & {
  siteMediaToken: string;
  topLevelMediaToken?: string;
  origin: string;
  openSource: string;
  namespace?: NameSpace;
  privateMediaRestAPI?: string;
  privateMediaRestFiles?: string;
  privateMediaRestVideoFiles?: string;
  publicMediaRestAPI?: string;
  publicMediaRestFiles?: string;
  publicMediaRestVideoFiles?: string;
  publicCMSDomain?: string;
  unsplashRestAPI?: string;
  pixAPI?: string;
  shutterStockPaymentAPI?: string;
  createOrderPageDomain?: string;
  recommendedRestApi?: string;
  recommendedPreferencesRestApi?: string;
  recommendedTopicsListApiUrl?: string;
  additionalItemAttributes?: string[];
  path?: string;
  appendTo?: string;
  sort?: Sort;
  layout?: SettingsLayout;
  mediaType?: string;
  maxParallelUploads?: number;
  videoEncodingProfile?: string;
  useFastTranscoding?: boolean;
  acl?: Acl;
};

export type TIncomingTranslationsObject = {
  header?: string;
  'footer.submit'?: string;
  [key: string]: string;
};

export type TTranslationsObject = {
  'common.header'?: string;
  'actions.choose-files'?: string;
  [key: string]: string;
};

export type TUserSettings = {
  hasVisitorUploads: boolean;
  hasContributors: boolean;
  sitesNumber: number;
  hasMultipleSites: boolean;
  hasWixVideoUploads?: boolean;
};

export type TCustomData = {
  sections: {
    title: string;
    data: Partial<MediaManagerSDKFile>[];
    action?: {
      url?: string;
      title: string;
    };
  }[];
};

export type TIncomingMMGRSettings = TMMGRCommonSettings &
  Partial<{
    vision: string | string[];
    translations: string | TIncomingTranslationsObject;
    tabsList: string | string[];
    servicesList: string | ExternalSource[];
    selectionType: Selection;
    canShowSelectButton: boolean;
    canSubmitUntranscoded: boolean;
    canSubmitTransparentVideos: boolean;
    uploadItems: boolean;
    uploadItemsOpen: boolean;
    initialMediaTypes: string | MediaType[];
    customData: TCustomData;
    restrictContent: boolean;
    restrictK12: boolean;
    publicMediaRoot: string | WixMediaRoot[];
    wixelPublicMediaRoot: string | WixMediaRoot[];
    editor3PublicMediaRoot: string | WixMediaRoot[];
    metaSiteToken: string;
    team: MediaManagerSDKTeam;
    mimeTypesAllowed: string[];
  }>;

export interface IExperimentsSettings {
  isEmailWithWixDomain: boolean;
  isCantBuyOnShutterstockExperimentEnabled: boolean;
  isNewCheckoutExperimentEnabled: boolean;
  isNTTExperimentEnabled: boolean;
  isPublicMediaScoringProfileEnabled: boolean;
  isShowKeysExperimentEnabled: boolean;
  isOnlyFavsBoardExperimentEnabled: boolean;
  isPreventFontsUploadExperimentEnabled: boolean;
  forceUploadErrorsExperimentValue: string;
  isDisableUploadExperimentEnabled: boolean;
  isLinkToPublicCMSExperimentEnabled: boolean;
  isYellExperimentEnabled: boolean;
  isForceYellExperimentEnabled: boolean;
  isAITextToImageEnabled: boolean;
  storageQuotaVisibilityAndEnforcementExperiment: 'false' | 'enforcement';
  storageQuotaGracePeriodExperimentEnabled: boolean;
  isManageStorageButtonEnabled: boolean;
  isSearchAndSortInFilesImporterEnabled: boolean;
  isUIAlignmentsEnabled: boolean;
  isLimitMimeTypesEnabled: boolean;
  isFileAwarenessEnabled: boolean;
  isPlatformizationTypesCheckEnabled: boolean;
  isPlatformizedApiEnabled: boolean;
  isHideInstagramEnabled: boolean;
  isHideFlickrEnabled: boolean;
  isUseSmallerLimitForPublicSearchEnabled: boolean;
  isHideGooglePhotosEnabled: boolean;
  isIgnoreEmptyRouteEnabled: boolean;
  isHideAdobeExpressEnabled: boolean;
  isWixSession3Enabled: boolean;
  isCopyWixUrlEnabled: boolean;
  isForceWixelImportEnabled: boolean;
}

export type TMMGRSettings = TMMGRCommonSettings &
  IExperimentsSettings &
  TUserSettings &
  MediaManagerSDKSitesInfo &
  MediaManagerSDKManagerInfo &
  Partial<{
    translations: TTranslationsObject;
    addMediaButtonTranslationId: string;
    siteFilesTranslationId: string;
    vision: string[];
    tabsList: MainSource[];
    servicesList: ExternalSource[];
    externalSources: ExternalSource[];
    initialMediaTypes: MediaType[];
    selection: Selection;
    isSecureContext: boolean;
    restrictGenAIFeatures?: boolean;
    canSubmitItems: boolean;
    addMediaButtonVisible: boolean;
    addMediaPanelOpened: boolean;
    hideCreateAndEditActions: boolean;
    hideShutterstock: boolean;
    hideMediaStorageWidget: boolean;
    hideUnsplash: boolean;
    disableTeams: boolean;
    customData: TCustomData;
    isNewUser: boolean;
    disableOrientationFilters: boolean;
    isExistingUserWithOldMediaOnly: boolean;
    isExistingUserWithOldMediaOnlyWithMultipleSites: boolean;
    hasVisitorUploads: boolean;
    hasContributors: boolean;
    hasSiteMedia: boolean;
    hasOldMedia: boolean;
    restrictContent: boolean;
    isEmailWithWixDomain: boolean;
    publicMediaRoot: WixMediaRoot[];
    metaSiteToken: string;
    team: MediaManagerSDKTeam;
    restrictK12: boolean;
    onlyCurrentUser: boolean;
    mimeTypesAllowed: string[];
  }>;

export type TMMGRAdapterSettings = Partial<{
  apiUrl: string;
  filesUrl: string;
  videoFilesUrl: string;
  siteMediaToken: string;
  topLevelMediaToken: string;
  metaSiteToken: string;
  namespace: NameSpace;
  isSecureContext: boolean;
  customData: TCustomData;
  siteFilesSettings: Omit<TMMGRAdapterSettings, 'siteFilesSettings'>;
  favoritesSettings: Omit<TMMGRAdapterSettings, 'favoritesSettings'>;
  wixMediaSettings: TWixMediaAdapterSettings;
  unsplashSettings: TUnsplashAdapterSettings;
  shutterstockSettings: TShutterstockAdapterSettings;
  recommendedHomeSettings: Omit<TMMGRAdapterSettings, 'recommendedHomeSettings'>;
  publicMediaRoot?: WixMediaRoot[];
  useNewBoardEndpoints?: boolean;
  tabsList: MainSource[];
  uploadLimits: {
    default: number;
  } & { [key in MediaType]?: number };
  prefetchNextPage: boolean;
  isTeam: boolean;
  parentAccountId: string;
  cacheKiller: boolean;
  onlyCurrentUser: boolean;
  isPublicMediaScoringProfileEnabled: IExperimentsSettings['isPublicMediaScoringProfileEnabled'];
  country: string;
  isWixel: boolean;
}>;

export type TUnsplashAdapterSettings = Omit<TMMGRAdapterSettings, 'unsplashSettings'> & {
  language: string;
};

export type TShutterstockAdapterSettings = Omit<TMMGRAdapterSettings, 'shutterstockSettings'> & {
  country: string;
};

type TWixMediaAdapterSettings = Omit<TMMGRAdapterSettings, 'wixMediaSettings'> & {
  restrictContent: boolean;
  restrictK12: boolean;
  language: string;
  hideShutterstock: boolean;
  hideUnsplash: boolean;
};

export interface IIncomingMMGRSettings {
  mmgrSettings: TIncomingMMGRSettings;
}

export interface IMMGRSettingsProps {
  latestIncomingMMGRSettings: TIncomingMMGRSettings;
  mmgrSettings: TMMGRSettings;
}

export interface IUserSettingsProps {
  userSettings: TUserSettings;
}
